<template>
  <main class="flex-display-body container py-5">
    <page-loading-wrapper :is-loading="loading">
      <!-- slot-scope must be added here to force compilation to wait until the isLoading to become false. -->
      <template slot-scope="props">
        <b-alert v-if="errorResponse"
                 id="group-details-error-alert"
                 variant="warning"
                 show
        >
          {{errorResponse}}
        </b-alert>
        <div v-else>
          <delete-group-confirm-modal modal-id="dashbd-delete-group-confirm"
                                      :group-id="managedGroup.uugid"
                                      @deleteGroup="onConfirmDeleteGroup">
          </delete-group-confirm-modal>
          <div class="row" id="main-content">
            <div class="col-12">
              <div id="opaque-overlay" v-if="submittingGroupDelete"></div>
              <div class="row mb-4">
                <div class="col-md-8 wrap-words">
                  <h1 class="display-4">
                    Dashboard for <span class="font-weight-bolder">{{ managedGroup.uugid }}</span>
                  </h1>
                </div>
                <div v-if="isAdminOfCurrentGroup" class="col-md-4">
                  <router-link
                    tag="button"
                    class="btn btn-success float-md-right"
                    id="btn-create-new-group"
                    :to="{name: 'CreateGroup', params: {stem: managedGroup.uugid} }">
                    &plus; Create New Group
                  </router-link>
                </div>
              </div>
              <template v-if="isAdminOrManagerOfCurrentGroup">
                <div role="tablist"
                     class="d-flex flex-column flex-sm-row justify-content-between"
                     :aria-label="'Actions for ' + managedGroup.uugid">
                  <button role="tab"
                          aria-controls="group-details-page"
                          id="groupDetails"
                          type="button"
                          class="btn btn-block"
                          @click="tabSelected='appGroupDetail'"
                          :class="tabSelected === 'appGroupDetail' ? 'btn-primary' : 'btn-link'"
                          :aria-selected="tabSelected === 'appGroupDetail'">
                    Details
                  </button>
                  <template v-if="isAdminOfCurrentGroup">
                    <button role="tab"
                            aria-controls="group-edit-page"
                            id="groupEdit"
                            type="button"
                            class="btn btn-block"
                            @click="tabSelected='appEditGroup'"
                            :class="tabSelected === 'appEditGroup' ? 'btn-primary' : 'btn-link'"
                            :aria-selected="tabSelected === 'appEditGroup'">
                      Edit Group
                    </button>
                  </template>
                  <button role="tab"
                          aria-controls="add-members-page"
                          id="addMembers"
                          type="button"
                          class="btn btn-block"
                          @click="tabSelected='appAddMembers'"
                          :class="tabSelected === 'appAddMembers' ? 'btn-primary' : 'btn-link'"
                          :aria-selected="tabSelected === 'appAddMembers'">
                    Add Members
                  </button>
                  <button role="tab"
                          aria-controls="edit-members-page"
                          id="editMembers"
                          type="button"
                          class="btn btn-block"
                          @click="tabSelected='appEditMembers'"
                          :class="tabSelected === 'appEditMembers' ? 'btn-primary' : 'btn-link'"
                          :aria-selected="tabSelected === 'appEditMembers'">
                    Edit Members
                  </button>
                </div>
              </template>
              <transition appear name="slide" mode="out-in">
                <keep-alive>
                  <component
                    :is="tabSelected" :uugid="managedGroup.uugid"
                    :managedGroup="managedGroup" @changeView="setView($event)">
                  </component>
                </keep-alive>
              </transition>
            </div>
            <div class="col-12">
              <b-btn v-b-modal.dashbd-delete-group-confirm
                     v-if="!isEmpty(managedGroup) && isAdminOfCurrentGroup"
                     id="btn-delete-group-dashboard"
                     variant="danger"
                     :disabled="submittingGroupDelete">
                {{submittingGroupDelete ? 'Submitting...' : 'Delete Group'}}
              </b-btn>
            </div>
            <div class="col-12 text-secondary mt-2">
              Group {{managedGroup.uugid}}
            </div>
          </div>
        </div>
      </template>
    </page-loading-wrapper>
  </main>
</template>

<script>
import { eventBus } from '../main';
import GroupDetail from './GroupDetail';
import EditGroup from './edit-group/EditGroup';
import AddMembers from './add-members/AddMembers';
import EditMembers from './EditMembers';
import PageLoadingWrapper from './slots/PageLoadingWrapper';
import { auth, groupsMixin, util } from '../util/groups-mixin';
import { deleteGroup } from '../util/delete-group-mixin';

export default {
  mixins: [auth, deleteGroup, groupsMixin, util],
  data() {
    return {
      tabSelected: this.selectedComponent,
      loading: false,
      errorResponse: '',
    };
  },
  components: {
    appGroupDetail: GroupDetail,
    appEditGroup: EditGroup,
    appAddMembers: AddMembers,
    appEditMembers: EditMembers,
    PageLoadingWrapper,
  },
  props: {
    selectedComponent: {
      type: String,
      default: 'appGroupDetail',
    },
  },
  methods: {
    setView(link) {
      this.tabSelected = link;
    },
  },
  beforeDestroy() {
    /* Reset current group before navigating away. */
    this.$store.commit('setCurrentGroup', {});
  },
  created() {
    const vm = this;
    eventBus.clearStatus();
    vm.loading = true;
    this.$store.dispatch('fetchDetailsAndSetCurrentGroup', vm.$route.params.id)
      .catch((error) => {
        // Clear global exception messages so multiple aren't shown
        eventBus.clearStatus();
        if (error.message) {
          this.errorResponse = error.message;
        } else {
          this.errorResponse = 'Unknown error occurred.';
        }
        vm.$store.commit('setCurrentGroup', {});
      })
      .finally(() => {
        vm.loading = false;
        vm.scrollToElement('app-header');
      });
  },
};
</script>

<style>
  /*noinspection CssUnusedSymbol*/
  .slide-enter {
    opacity: 0;
  }

  /*noinspection CssUnusedSymbol*/
  .slide-enter-active {
    animation: slide-in 0.3s ease-in forwards;
    transition:opacity 0.3s;
  }

  /*noinspection CssUnusedSymbol*/
  .slide-leave-to {
    animation: slide-out 0.3s ease-in forwards;
    transition: opacity 0.3s;
    opacity: 0;
  }

  #opaque-overlay {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0,0,0,0.5);
    z-index: 2;
  }

  @keyframes slide-in {
    from {
      transform: translateX(100px);
    }
    to {
      transform: translateX(0px);
    }
  }

  @keyframes slide-out {
    from {
      transform: translateX(0px);
    }
    to {
      transform: translateX(100px);
    }
  }

  .font-weight-bolder {
    font-weight: bolder;
  }
</style>



// WEBPACK FOOTER //
// src/components/GroupDashboard.vue